// The DialStack bridge lives in hero-builders, not crm-core, so it gets plain fetch with crm-core's auth headers.
import Constants from 'Constants'
import { getToken } from 'providers/firebaseAuth'

export interface BridgeSession {
  clientSecret: string
  expiresAt: string
  publishableKey: string
  demoControls: boolean
}

export interface CallerMatch {
  accountId: string
  accountName: string
  accountNumber: number | null
  contactName: string
  phone: string
  serviceAddress: string
}

export interface DemoCaller {
  name: string
  contactName: string | null
  fromNumber: string
}

const bridgeFetch = async (businessId: string, path: string, init: RequestInit = {}): Promise<Response> => {
  const token = await getToken()
  return fetch(`${Constants.DIALSTACK_BRIDGE_URL}${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      authorization: `Bearer ${token}`,
      [Constants.BUSINESS_IDENTIFIER]: businessId,
    },
  })
}

const readJson = async <T>(res: Response): Promise<T> => {
  if (!res.ok) {
    throw new Error(`DialStack bridge ${res.status}`)
  }
  return res.json()
}

// 404 means the bridge has no DialStack account for this business, so the screen pop stays off.
export const createSession = async (businessId: string): Promise<BridgeSession | null> => {
  const res = await bridgeFetch(businessId, '/v1/session', { method: 'POST' })
  if (res.status === 404) {
    return null
  }
  return readJson<BridgeSession>(res)
}

export const lookupCaller = async (businessId: string, phone: string): Promise<CallerMatch[]> => {
  const res = await bridgeFetch(businessId, `/v1/lookup?phone=${encodeURIComponent(phone)}`)
  const { matches } = await readJson<{ matches: CallerMatch[] }>(res)
  return matches
}

export const getDemoCallers = async (businessId: string): Promise<DemoCaller[]> => {
  const res = await bridgeFetch(businessId, '/v1/demo-callers')
  const { callers } = await readJson<{ callers: DemoCaller[] }>(res)
  return callers
}

export const simulateCall = async (businessId: string, caller: DemoCaller): Promise<void> => {
  const res = await bridgeFetch(businessId, '/v1/simulate', {
    method: 'POST',
    body: JSON.stringify({ fromNumber: caller.fromNumber, fromName: caller.contactName }),
  })
  await readJson(res)
}
